import React, { useEffect, useState, useCallback } from "react";
import { X, Mail, Gift } from "lucide-react";
import { api } from "../lib/api";

/**
 * ExitIntentPopup — appears once per session when the user is about to leave.
 * Desktop trigger: mouse moves toward the top edge of the viewport.
 * Mobile fallback: after 45s of engagement, or fast scroll-up after scrolling down.
 * Suppressed on admin routes and if the user is already subscribed / dismissed.
 */
const STORAGE_KEY = "bdd_exit_popup_v1";
const DISMISS_TTL_DAYS = 14;

function isSuppressed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { ts, kind } = JSON.parse(raw);
    if (kind === "subscribed") return true;
    const days = (Date.now() - ts) / 86400000;
    return days < DISMISS_TTL_DAYS;
  } catch {
    return false;
  }
}

function persist(kind) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), kind }));
  } catch {
    // ignore
  }
}

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true); // pre-checked for one-click
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  // Skip entirely on admin routes
  const onAdminRoute =
    typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  const trigger = useCallback(() => {
    if (open || isSuppressed()) return;
    setOpen(true);
  }, [open]);

  useEffect(() => {
    if (onAdminRoute) return;
    if (isSuppressed()) return;

    // Desktop: mouse leaves top of viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        trigger();
      }
    };

    // Mobile fallback: after long engagement + scroll-up gesture
    let lastY = window.scrollY;
    let scrolledDown = false;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY + 40) scrolledDown = true;
      if (scrolledDown && y < lastY - 80 && y < 300) {
        trigger();
      }
      lastY = y;
    };

    // Time-based fallback: 60s of stay-on-site
    const timer = setTimeout(() => trigger(), 60_000);

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, [trigger, onAdminRoute]);

  const close = (kind = "dismissed") => {
    persist(kind);
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setState({ loading: false, ok: false, error: "Serve il consenso marketing." });
      return;
    }
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/newsletter/subscribe", {
        email: email.trim().toLowerCase(),
        marketing_consent: true,
        source: "exit-popup",
      });
      setState({ loading: false, ok: true, error: "" });
      persist("subscribed");
      // auto-close after a short beat so user sees confirmation
      setTimeout(() => setOpen(false), 3000);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Non è stato possibile completare l'iscrizione.";
      setState({ loading: false, ok: false, error: msg });
    }
  };

  if (!open || onAdminRoute) return null;

  return (
    <div
      data-testid="exit-popup"
      className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={() => close("dismissed")}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-3xl overflow-hidden bg-zinc-950 border border-zinc-800 shadow-2xl"
      >
        {/* Decorative gold ribbon */}
        <div className="h-1.5 bg-gradient-to-r from-[#FBD51C] via-[#F5C518] to-[#FBD51C]" />

        <button
          onClick={() => close("dismissed")}
          data-testid="exit-popup-close"
          aria-label="Chiudi"
          className="absolute right-4 top-6 text-zinc-500 hover:text-white z-10"
        >
          <X size={22} />
        </button>

        <div className="p-8 sm:p-10">
          {state.ok ? (
            <div data-testid="exit-popup-success" className="text-center py-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#FBD51C]/15 flex items-center justify-center mb-4">
                <Mail className="text-[#FBD51C]" size={26} />
              </div>
              <h3 className="font-display font-black text-2xl text-white mb-2">
                Ci sei!
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Ti ho mandato un&apos;email di benvenuto. Ogni venerdì mattina
                riceverai le migliori offerte della settimana.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <Gift size={16} className="text-[#FBD51C]" />
                <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C]">
                  Aspetta un secondo
                </div>
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-white leading-[1.05] mb-3">
                Non perderti le{" "}
                <span className="text-[#FBD51C]">migliori offerte</span> della
                settimana.
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                Ogni <b className="text-white">venerdì mattina</b>, una email
                con i set con lo sconto più alto. Niente spam, disiscrizione
                con un click.
              </p>

              <form onSubmit={submit} className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    placeholder="tua@email.com"
                    data-testid="exit-popup-email"
                    autoFocus
                    className="flex-1 bg-black/40 border border-zinc-800 focus:border-[#FBD51C] outline-none rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600"
                  />
                  <button
                    type="submit"
                    disabled={state.loading || !consent}
                    data-testid="exit-popup-submit"
                    className="bg-[#FBD51C] hover:bg-[#F5C518] disabled:bg-zinc-800 disabled:text-zinc-500 text-black font-black text-sm uppercase tracking-wider px-6 py-3 rounded-xl transition-colors whitespace-nowrap"
                  >
                    {state.loading ? "…" : "Iscrivimi"}
                  </button>
                </div>

                <label className="flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(ev) => setConsent(ev.target.checked)}
                    data-testid="exit-popup-consent"
                    className="mt-0.5 accent-[#FBD51C]"
                  />
                  <span>
                    Acconsento a ricevere la newsletter di BrickDailyDeals.
                    Leggi la{" "}
                    <a href="/privacy" className="underline hover:text-[#FBD51C]">
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>

                {state.error && (
                  <div
                    data-testid="exit-popup-error"
                    className="text-xs text-rose-400"
                  >
                    {state.error}
                  </div>
                )}
              </form>

              <button
                onClick={() => close("dismissed")}
                data-testid="exit-popup-decline"
                className="mt-5 text-[11px] text-zinc-600 hover:text-zinc-400 underline"
              >
                No grazie, continuo senza iscrivermi
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
